import { computed, ref } from 'vue';
import { useAuthStore } from '@/shared/stores/auth';
import { useProfileStore } from '@/shared/stores/profile';
import { useProjectStore } from '@/shared/stores/project';
import {
  clearRefreshExpiryCredentialReconciliationPending,
  purgeAdamCloudStorage,
  rememberSuccessfulAuthCredential,
  shouldPurgeStorageForCredentialMismatch,
} from '@/shared/utils/authSessionCleanup';

// Shared state for redirection across all useAuth instances
const isRedirecting = ref(false);

/**
 * Authentication composable that orchestrates auth and profile stores
 * Maintains proper separation of concerns and avoids circular dependencies
 */
export function useAuth() {
  const authStore = useAuthStore();
  const profileStore = useProfileStore();
  const projectStore = useProjectStore();

  // Computed properties for reactive state
  const isAuthenticated = computed(() => {
    if (!authStore.accessToken || !authStore.user) return false;
    const expiresAt = authStore.user.expiresAt;
    if (!expiresAt) return false;
    const expires = typeof expiresAt === 'string' ? new Date(expiresAt) : expiresAt;
    return expires.getTime() > Date.now();
  });

  const user = computed(() => authStore.user);
  const profile = computed(() => profileStore.profile);
  const loading = computed(() => authStore.loading || profileStore.loading || isRedirecting.value);
  const error = computed(() => authStore.error || profileStore.error);

  function setRedirecting(value: boolean) {
    isRedirecting.value = value;
  }

  /**
   * Handles the complete login flow
   * 1. Authenticate user via auth store
   * 2. Load user profile if authentication successful
   */
  async function login(email: string, password: string): Promise<boolean> {
    const shouldPurgePersistedState = shouldPurgeStorageForCredentialMismatch(email);
    if (shouldPurgePersistedState) {
      profileStore.clearProfile();
      projectStore.clearSelectedProjectCache();
      projectStore.resetSelectionValidation();
      purgeAdamCloudStorage();
    }

    const success = await authStore.login(email, password);

    if (success) {
      rememberSuccessfulAuthCredential(email);
      clearRefreshExpiryCredentialReconciliationPending();

      // Load user profile after successful authentication
      try {
        await profileStore.loadProfile(true);
      } catch (error) {
        // Profile loading failure doesn't break the login flow
        console.warn('Failed to load user profile after login:', error);
      }
    }
    
    return success;
  }

  /**
   * Handles the complete logout flow
   * 1. Logout via auth store
   * 2. Clear in-memory user/project state
   * 3. Purge persisted ADAM Cloud browser storage
   */
  async function logout(): Promise<void> {
    try {
      await authStore.logout();
    } finally {
      profileStore.clearProfile();
      projectStore.clearSelectedProjectCache();
      projectStore.resetSelectionValidation();
      clearRefreshExpiryCredentialReconciliationPending();
      purgeAdamCloudStorage();
    }
  }

  /**
   * Refreshes authentication token
   */
  async function refresh(): Promise<boolean> {
    return await authStore.refresh();
  }

  /**
   * Initializes authentication state on app startup
   * If user has valid tokens, loads their profile
   */
  async function initialize(): Promise<void> {
    // If we have tokens but no user profile loaded, fetch it
    if (authStore.accessToken && isAuthenticated.value && !profileStore.profile.id) {
      try {
        await profileStore.loadProfile(true);
      } catch (error) {
        // If profile loading fails, the user might need to re-authenticate
        console.warn('Failed to load profile on initialization:', error);
      }
    }
  }

  /**
   * Fetches current user data and updates both auth and profile stores
   */
  async function fetchCurrentUser(includeProjectMemberships = false): Promise<boolean> {
    const userDto = await authStore.fetchCurrentUser(includeProjectMemberships);
    
    if (userDto) {
      // Update profile store with the fetched data
      try {
        await profileStore.loadProfile(includeProjectMemberships);
        return true;
      } catch (error) {
        console.warn('Failed to update profile after fetching current user:', error);
        return false;
      }
    }
    
    return false;
  }

  return {
    // State
    isAuthenticated,
    user,
    profile,
    loading,
    error,
    
    // Actions
    login,
    logout,
    refresh,
    initialize,
    fetchCurrentUser,
    setRedirecting,
  };
}
